import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import MobileSheetAutoComplete from '../../../components/ui/MobileSheetAutoComplete'
import { useEmployeeSearch } from '../../minutes/hooks/useEmployeeSearch'
import type { EmployeePick } from '../../minutes/types'
import type { MinutePerson, PersonInput } from '../types'

interface Props {
  /** Seçili kişi (tekil) — atanmamışsa null. */
  person: MinutePerson | null
  editable: boolean
  /** Temizlemeye izin var mı? Zorunlu alanlarda false (yalnız değiştirilebilir). */
  canClear: boolean
  onSet: (p: PersonInput) => void
  onClear: () => void
  placeholder: string
  /** Boş ipucu; verilmezse (popup) hiç gösterilmez. */
  emptyText?: string
  removeLabel: string
  /** true → kişi atanmışken seçici gizlenir (ikinci ekleme engellenir); kaldırınca yeniden görünür. */
  disableWhenSet?: boolean
}

/**
 * Tekil kişi seçici — HR aramasından tek kişi atanır/değiştirilir. Alt aksiyonun <b>Atanan</b> alanında
 * kullanılır (çoklu seçim için {@code ActionPeopleEditor}).
 *
 * <p>Bu bileşen 2026-08-12'ye kadar {@code ActionCoordinatorEditor} adını taşıyordu; koordinatör özelliği
 * kaldırılınca rolüne göre nötr ada taşındı (davranışı değişmedi).
 */
export default function SinglePersonEditor({
  person,
  editable,
  canClear,
  onSet,
  onClear,
  placeholder,
  emptyText,
  removeLabel,
  disableWhenSet = false,
}: Readonly<Props>) {
  const { i18n } = useTranslation()
  const { suggestions, search } = useEmployeeSearch()
  const [query, setQuery] = useState('')

  const complete = (e: AutoCompleteCompleteEvent) => search(e.query)
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v = e.value
    setQuery(typeof v === 'string' ? v : ((v as EmployeePick)?.fullName ?? ''))
  }
  const onSelect = (e: AutoCompleteSelectEvent) => {
    const p = e.value as EmployeePick
    if (p?.employeeId && p.employeeId !== person?.employeeId) {
      onSet({
        employeeId: p.employeeId,
        firstName: p.firstName,
        lastName: p.lastName,
        businessMail: p.businessMail,
        jobTitleTr: p.jobTitleTr,
        jobTitleEn: p.jobTitleEn,
      })
    }
    setQuery('')
  }

  // Tekil: bir kişi atanmışken (disableWhenSet) seçici gizlenir → ikinci ekleme engellenir; kaldırınca döner.
  const showPicker = editable && !(disableWhenSet && person != null)

  return (
    <div className="act-followers">
      {showPicker && (
        <MobileSheetAutoComplete<EmployeePick>
          value={query}
          suggestions={suggestions}
          completeMethod={complete}
          field="fullName"
          delay={300}
          minLength={2}
          placeholder={placeholder}
          onChange={onQueryChange}
          onSelect={onSelect}
          panelClassName="minutes-participant-panel"
          itemTemplate={(item: EmployeePick) => (
            <div className="minutes-emp-item">
              <span className="minutes-emp-name">{item.fullName}</span>
              {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
              <span className="minutes-emp-sicil">{item.employeeId}</span>
            </div>
          )}
          className="minutes-participant-input"
        />
      )}

      {person == null ? (
        emptyText ? <p className="act-empty-hint">{emptyText}</p> : null
      ) : (
        <ul className="act-follower-list">
          <li className="act-follower-chip">
            <span className="act-follower-name">{person.fullName}</span>
            <span className="act-follower-title">
              {(i18n.language === 'en' ? person.jobTitleEn : person.jobTitleTr) ??
                person.jobTitleTr ??
                ''}
            </span>
            {editable && canClear && (
              <button
                type="button"
                className="act-follower-remove"
                aria-label={removeLabel}
                title={removeLabel}
                onClick={onClear}
              >
                <i className="pi pi-times" aria-hidden="true" />
              </button>
            )}
          </li>
        </ul>
      )}
    </div>
  )
}
